import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { api } from '@/api/client';
import type {
  CreateQuotaReq,
  CreateQuotaResp,
  GetQuotasResp,
  GetResourcesResp,
  Quota,
  QuotaSelectionOperator,
} from '@/api/types';
import QuotaForm, {
  type QuotaFormValues,
} from '@/components/resources/QuotaForm';
import { extractApiMessage } from '@/components/resources/form/errors';

function quotaToFormValues(q: Quota): QuotaFormValues {
  return {
    name: q.name,
    description: q.description,
    selectorObject: q.selector_object,
    selectorOperator: q.selection_operator as QuotaSelectionOperator,
    selectorRegex: q.selector_regex,
    resource: q.resource,
    quota: q.quota,
  };
}

export default function ModifyQuotaPage() {
  const { name: nameParam = '' } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<QuotaFormValues | null>(null);

  const {
    data: quotaData,
    isLoading: quotaLoading,
    error: quotaError,
  } = useQuery({
    queryKey: ['quota', nameParam],
    queryFn: () =>
      api<GetQuotasResp>(`/v1/quotas/${encodeURIComponent(nameParam)}`),
    enabled: nameParam !== '',
  });

  const { data: resourcesData, isLoading: resourcesLoading } = useQuery({
    queryKey: ['resources'],
    queryFn: () => api<GetResourcesResp>('/v1/resources'),
  });

  // The GET endpoint returns the single quota wrapped in `quotas: [...]`
  // (mirroring the resource GET shape). Seed local form state from the
  // first response and don't overwrite the user's edits on subsequent
  // refetches.
  const quota = quotaData?.quotas?.[0];
  useEffect(() => {
    if (quota && values === null) {
      setValues(quotaToFormValues(quota));
    }
  }, [quota, values]);

  const updateMutation = useMutation({
    mutationFn: (req: CreateQuotaReq) =>
      api<CreateQuotaResp>(`/v1/quotas/${encodeURIComponent(nameParam)}`, {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotas'] });
      queryClient.invalidateQueries({ queryKey: ['quota', nameParam] });
      navigate('/quotas');
    },
  });

  const submitError = updateMutation.error
    ? extractApiMessage(updateMutation.error)
    : null;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
        >
          <Link
            to="/quotas"
            className="hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Quotas
          </Link>
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          <span className="text-foreground truncate max-w-[40ch]">
            {nameParam}
          </span>
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          <span className="text-foreground">Modify</span>
        </nav>
        <h1 className="text-2xl font-semibold">
          Modify Quota
          <span className="ml-2 font-mono text-base text-muted-foreground">
            {nameParam}
          </span>
        </h1>
      </div>

      {quotaLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">
          Loading quota…
        </div>
      )}

      {quotaError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Failed to load quota</div>
            <div className="text-xs mt-0.5 opacity-90">
              {extractApiMessage(quotaError)}
            </div>
          </div>
        </div>
      )}

      {values && (
        <QuotaForm
          mode="modify"
          values={values}
          onChange={setValues}
          resources={resourcesData?.resources ?? []}
          resourcesLoading={resourcesLoading}
          onSubmit={(req) => updateMutation.mutate(req)}
          submitting={updateMutation.isPending}
          submitError={submitError}
          cancelHref="/quotas"
        />
      )}
    </div>
  );
}
